import { DesignScheme } from '@data/buildingProjects/DesignSchemes/designScheme';
import { FloorBased } from '@data/buildingProjects/DesignSchemes/FloorBased/FloorBased';
import { DesignSchemeSetManager } from '@data/buildingProjects/DesignSchemeSetManager';
import FVector from '@data/geometry/FVector';
import { PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline';
import usePersistentStore, { getDesignSchemesState } from '@gui/store/persistent/persistentStore';
import { ControllerManager } from '@gui/controller/ControllerManager';
import {
    BorderSource,
    DeepPartial,
    DELETE_FROM_STORE,
    DesignSchemeSetState,
    DesignSchemeSetType,
    DesignSchemeState,
    FootprintState,
    GlobalDesignSchemeState,
    LayerKind,
    SchemeKey,
} from '@visualizer/types';
import { requireApp } from 'store/assertions';
import { getGrid, LoadingStatus, useLoadingStore } from 'store/LoadingStore';
import { getViewer } from 'store/viewerStore';
import { DesignSchemePostBlockSync, RefreshDesignSchemePostBlocksOptions } from './designSchemes/DesignSchemePostBlockSync';
import { DesignSchemeRuntimeSync } from './designSchemes/DesignSchemeRuntimeSync';

/**
 * Public DesignSchemes façade used by the rest of the GUI.
 * It coordinates store updates, manager mutations, runtime scheme subscriptions,
 * and delegates the hidden pipeline post-block syncing to focused helper classes.
 *
 */
class DesignSchemeController {
    designSchemeSetManager: DesignSchemeSetManager;
    private readonly runtimeSync: DesignSchemeRuntimeSync;
    private readonly postBlockSync: DesignSchemePostBlockSync;
    private readonly refreshGeomRequestBySet = new Map<string, number>();

    constructor() {
        const initialState = getDesignSchemesState();
        this.designSchemeSetManager = DesignSchemeSetManager.getInstance(initialState);
        this.runtimeSync = new DesignSchemeRuntimeSync((updates) => this.update(updates));
        this.postBlockSync = new DesignSchemePostBlockSync();

        // Subscribe all existing schemes to changes in the store:
        this.designSchemeSetManager.getProjects().forEach((dsSet) => {
            dsSet.getDesignSchemes().forEach((ds) => {
                this.subscribeScheme(ds);
            });
        });

        // Refresh the blocks on startup (e.g. when refreshing the page):
        const refreshOnStartup = () => {
            const selectedSetId = this.store.designSchemes.selectedSetId;
            const selectedSetState = selectedSetId ? this.store.designSchemes.sets[selectedSetId] : undefined;
            if (!selectedSetId || !selectedSetState) return;

            this.refreshDesignSchemePostBlocks({
                refreshGeometry: !!selectedSetState.shadow || !!selectedSetState.keepout || !!selectedSetState.closeToDesignSchemes,
                refreshSensors: true,
            });
        };

        if (useLoadingStore.getState().loadingStatus === LoadingStatus.VISUALIZER_READY) {
            refreshOnStartup();
        } else {
            const unsubscribe = useLoadingStore.subscribe((state) => {
                if (state.loadingStatus !== LoadingStatus.VISUALIZER_READY) return;
                unsubscribe();
                refreshOnStartup();
            });
        }
    }

    private get store() {
        return usePersistentStore.getState();
    }

    private update(updates: DeepPartial<GlobalDesignSchemeState>, options?: { refreshPostBlocks?: RefreshDesignSchemePostBlocksOptions }) {
        this.store.setDesignSchemes(updates);
        getViewer().requestRender?.('ui');
        if (options?.refreshPostBlocks) {
            this.refreshDesignSchemePostBlocks(options.refreshPostBlocks);
        }
    }

    public subscribeScheme(scheme: DesignScheme): void {
        this.runtimeSync.subscribeScheme(scheme);
    }

    // =====================================
    // UPDATE HELPERS
    // =====================================

    public updateDefaults(partial: DeepPartial<GlobalDesignSchemeState['defaults']>) {
        this.update({
            defaults: partial,
        });
    }

    public updateDesignSchemeSetVisibility(setId: string, visible: boolean) {
        const dsSet = this.designSchemeSetManager.getProjectById(setId);
        if (!dsSet) return;

        dsSet.setvisibility(visible);
        this.update(
            {
                sets: {
                    [dsSet.getID()]: {
                        visibility: visible,
                    },
                },
            },
            {
                refreshPostBlocks: {
                    refreshGeometry: true,
                    refreshSensors: true,
                },
            }
        );
    }

    public updateSelectedDesignSchemeSet(partial: string | undefined) {
        this.designSchemeSetManager.getProjects().forEach((dsSet) => {
            dsSet.setvisibility(dsSet.getID() === partial);
        });

        this.update(
            {
                sets: Object.fromEntries(
                    this.designSchemeSetManager.getProjects().map((dsSet) => [dsSet.getID(), { visibility: dsSet.getID() === partial }])
                ),
                selectedSetId: partial,
            },
            {
                refreshPostBlocks: {
                    refreshGeometry: !!partial,
                    refreshSensors: true,
                },
            }
        );
    }

    public updateDesignSchemeSet(setId: string, partial: DeepPartial<DesignSchemeSetState>) {
        const shouldSyncPostBlocks =
            partial !== null &&
            typeof partial === 'object' &&
            ('innerSP' in partial ||
                'outterSP' in partial ||
                'shadow' in partial ||
                'keepout' in partial ||
                'closeToDesignSchemes' in partial ||
                'selectedFootprintId' in partial);

        this.update(
            {
                sets: {
                    [setId]: partial,
                },
            },
            shouldSyncPostBlocks
                ? {
                      refreshPostBlocks: {
                          refreshGeometry:
                              ('shadow' in partial && partial.shadow !== undefined) ||
                              ('keepout' in partial && partial.keepout !== undefined) ||
                              ('closeToDesignSchemes' in partial && partial.closeToDesignSchemes !== undefined) ||
                              ('selectedFootprintId' in partial && partial.selectedFootprintId !== undefined),
                          refreshSensors: true,
                      },
                  }
                : undefined
        );

        if (!shouldSyncPostBlocks) return;
    }

    public updateFootprint(setId: string, fpId: string, partial: DeepPartial<FootprintState>) {
        this.update(
            {
                sets: {
                    [setId]: {
                        footprints: {
                            [fpId]: partial,
                        },
                    },
                },
            },
            {
                refreshPostBlocks: {
                    refreshGeometry: true,
                    refreshSensors: true,
                },
            }
        );
    }

    public updateDesignScheme(setId: string, fpId: string, schemeId: string, partial: DeepPartial<DesignSchemeState>) {
        const store = usePersistentStore.getState();
        const setState = store.designSchemes.sets[setId];
        if (!setState) return;

        // Apply changes. Required for creation of new scheme, not yet in state:
        this.update({
            sets: {
                [setId]: {
                    footprints: {
                        [fpId]: {
                            designSchemes: {
                                [schemeId]: partial,
                            },
                        },
                    },
                },
            },
        });

        const sourceScheme = setState.footprints?.[fpId]?.designSchemes?.[schemeId];
        if (!sourceScheme) {
            const selectedSetState = this.store.designSchemes.sets[setId];
            const shouldRefreshGeometry = selectedSetState?.shadow || selectedSetState?.keepout || selectedSetState?.closeToDesignSchemes;

            this.refreshDesignSchemePostBlocks({
                refreshGeometry: shouldRefreshGeometry,
                refreshSensors: shouldRefreshGeometry,
            });
            return;
        }

        // If the update includes ANY hand drawn border, treat this patch as non-consistent (avoids sync issues):
        const isDeletePatch = partial === DELETE_FROM_STORE || (!!partial && typeof partial === 'object' && '__delete' in partial);
        const floorBasedPatch = !isDeletePatch && partial && typeof partial === 'object' ? partial.floorBased : undefined;
        const borderPatch = floorBasedPatch && typeof floorBasedPatch === 'object' ? (floorBasedPatch as any).borderState : undefined;
        const hasPerFootprintHandDrawnPatch =
            !!borderPatch &&
            (Object.prototype.hasOwnProperty.call(borderPatch, 'handDrawnBorder') ||
                Object.prototype.hasOwnProperty.call(borderPatch, 'handDrawnExternalBorder') ||
                Object.prototype.hasOwnProperty.call(borderPatch, 'handDrawnInternalBorder'));

        // 1) Non-Consistent. Update only selected scheme in a single write:
        if (!setState.consistent || hasPerFootprintHandDrawnPatch) {
            // Refresh first the post blocks:
            const selectedSetState = this.store.designSchemes.sets[setId];
            const shouldRefreshGeometry = selectedSetState?.shadow || selectedSetState?.keepout || selectedSetState?.closeToDesignSchemes;

            this.refreshDesignSchemePostBlocks({
                refreshGeometry: shouldRefreshGeometry,
                refreshSensors: shouldRefreshGeometry,
            });

            return;
        }

        // 2) Consistent. Update all schemes in a single write to avoid unsynced schemes renders:
        const schemeKey = sourceScheme.schemeKey;
        const footprintsPatch: Record<string, { designSchemes: Record<string, DeepPartial<DesignSchemeState>> }> = {};

        Object.entries(setState.footprints ?? {}).forEach(([otherFpId, fpState]) => {
            Object.entries(fpState.designSchemes ?? {}).forEach(([otherSchemeId, otherScheme]) => {
                if (otherScheme.schemeKey !== schemeKey) return;

                if (!footprintsPatch[otherFpId]) {
                    footprintsPatch[otherFpId] = { designSchemes: {} };
                }
                footprintsPatch[otherFpId].designSchemes[otherSchemeId] = partial;
            });
        });

        // Update all footprints at once:
        this.update({
            sets: {
                [setId]: {
                    footprints: footprintsPatch,
                },
            },
        });

        // Update the post blocks:
        const selectedSetState = this.store.designSchemes.sets[setId];
        const shouldRefreshGeometry = selectedSetState?.shadow || selectedSetState?.keepout || selectedSetState?.closeToDesignSchemes;

        this.refreshDesignSchemePostBlocks({
            refreshGeometry: shouldRefreshGeometry,
            refreshSensors: shouldRefreshGeometry,
        });
    }

    public updateSchemeLayer(setId: string, fpId: string, schemeId: string, kind: LayerKind, layerId: string) {
        const store = usePersistentStore.getState();
        const setState = store.designSchemes.sets[setId];
        if (!setState) return;

        const sourceScheme = setState.footprints?.[fpId]?.designSchemes?.[schemeId];
        if (!sourceScheme) return;

        const schemeKey = sourceScheme.schemeKey;

        // 0) Helper function to apply layer change to a scheme:
        const apply = (targetFpId: string, targetSchemeId: string) => {
            const ds = this.getDesignScheme(targetSchemeId);
            if (!ds) return;

            const grid = getGrid();
            const layer = grid?.searchLayerByUID(layerId);
            if (!layer) return;

            ds.updateLayer(layer, kind);
            this.updateDesignScheme(setId, targetFpId, targetSchemeId, {
                linkedLayerIds: {
                    [kind]: layerId,
                },
            });
        };

        // 1) Non-Consistent, update layer for the SELECTED scheme:
        apply(fpId, schemeId);

        if (!setState.consistent) return;

        // 2) Consistent, update layer for ALL the schemes of this set:
        Object.entries(setState.footprints ?? {}).forEach(([otherFpId, fpState]) => {
            Object.entries(fpState.designSchemes ?? {}).forEach(([otherSchemeId, otherScheme]) => {
                if (otherScheme.schemeKey !== schemeKey) return;
                if (otherFpId === fpId && otherSchemeId === schemeId) return;

                apply(otherFpId, otherSchemeId);
            });
        });
    }

    // =====================================
    // REMOVE HELPERS
    // =====================================

    public removeDesignScheme(setId: string, fpId: string, dsId: string) {
        // Return if the DesignSchemeSet doesn't exist:
        const designSchemeSet = this.getDesignSchemeSet(setId);
        if (!designSchemeSet) return;

        // Dispose of it completely:
        this.runtimeSync.unsubscribeScheme(dsId);
        this.getDesignScheme(dsId)?.dispose();

        // Remove from Manager:
        designSchemeSet.removeDesignScheme(dsId);

        // Remove from State:
        this.update(
            {
                sets: {
                    [setId]: {
                        footprints: {
                            [fpId]: {
                                designSchemes: {
                                    [dsId]: DELETE_FROM_STORE,
                                },
                            },
                        },
                    },
                },
            },
            {
                refreshPostBlocks: {
                    refreshGeometry: true,
                    refreshSensors: false,
                },
            }
        );
    }

    public removeFootprint(setId: string, fpId: string) {
        // Return if the DesignSchemeSet doesn't exist:
        const designSchemeSet = this.getDesignSchemeSet(setId);
        if (!designSchemeSet) return;

        // Remove instances:
        designSchemeSet
            .getDesignSchemes()
            .filter((ds) => ds.getFootprintId() === fpId)
            .forEach((ds) => {
                this.removeDesignScheme(setId, ds.getFootprintId(), ds.getID());
            });

        // Remove instance from Manager:
        designSchemeSet.removeFootprint(fpId);

        // Remove from State:
        this.update({
            sets: {
                [setId]: {
                    footprints: {
                        [fpId]: DELETE_FROM_STORE,
                    },
                },
            },
        });

        // Update Selected Footprint properly:
        const fps = this.getFootprints(setId) ?? [];
        if (fps.length > 0) {
            this.updateDesignSchemeSet(setId, { selectedFootprintId: fps[0].id });
        } else {
            this.updateDesignSchemeSet(setId, { selectedFootprintId: null });
        }

        const didRefreshPostBlocks = this.refreshDesignSchemePostBlocks({
            refreshGeometry: true,
            refreshSensors: true,
        });

        // Force pipeline rerun if last footprint was removed:
        if (!didRefreshPostBlocks) {
            const remainingDesignSchemes = this.getDesignSchemes(setId) ?? [];
            if (remainingDesignSchemes.length === 0) {
                ControllerManager.getInstance().getPipelineController().run();
            }
        }
    }

    public removeDesignSchemeSet(setId: string) {
        // Return if the DesignSchemeSet doesn't exist:
        const designSchemeSet = this.getDesignSchemeSet(setId);
        if (!designSchemeSet) return;
        const isSelectedSet = this.store.designSchemes.selectedSetId === setId;

        // Remove instances:
        designSchemeSet.getDesignSchemes().forEach((ds) => {
            this.removeDesignScheme(setId, ds.getFootprintId(), ds.getID());
        });

        // Remove instance from Manager:
        this.designSchemeSetManager.removeProject(setId);

        // Remove from State:
        this.update({
            sets: {
                [setId]: DELETE_FROM_STORE,
            },
            selectedSetId: isSelectedSet ? undefined : this.store.designSchemes.selectedSetId,
        });

        if (isSelectedSet) {
            this.postBlockSync.disableAllDesignSchemePostBlocks({ refreshSensors: true });
        }
    }

    // =====================================
    // ADD HELPERS
    // =====================================

    private getConfigFromSchemeState(type: SchemeKey, state: DesignSchemeState) {
        switch (type) {
            case SchemeKey.FLOOR_BASED:
                return state.floorBased;
            case SchemeKey.CONVEX_HULL:
                return state.convexHull;
            case SchemeKey.ORIENTED_VOLUME:
                return state.orientedVolume;
            case SchemeKey.BOX:
                return state.box;
        }
    }

    private getExistingSchemeStateTemplate(setId: string, schemeKey: SchemeKey): DesignSchemeState | undefined {
        const setState = usePersistentStore.getState().designSchemes.sets[setId];
        if (!setState?.consistent) return undefined;

        for (const footprintState of Object.values(setState.footprints ?? {})) {
            const matchingScheme = Object.values(footprintState.designSchemes ?? {}).find((scheme) => scheme?.schemeKey === schemeKey);
            if (matchingScheme) {
                return JSON.parse(JSON.stringify(matchingScheme)) as DesignSchemeState;
            }
        }

        return undefined;
    }

    private buildDefaultSchemeState(key: SchemeKey, linkedLayerIds?: Partial<Record<LayerKind, string>>): DesignSchemeState {
        const defaults = getDesignSchemesState().defaults;

        switch (key) {
            case SchemeKey.FLOOR_BASED:
                return {
                    schemeKey: key,
                    visibility: true,
                    linkedLayerIds,
                    floorBased: JSON.parse(JSON.stringify(defaults.floorBased)),
                    convexHull: undefined,
                    orientedVolume: undefined,
                    box: undefined,
                };

            case SchemeKey.CONVEX_HULL:
                return {
                    schemeKey: key,
                    visibility: true,
                    linkedLayerIds,
                    convexHull: JSON.parse(JSON.stringify(defaults.convexHull)),
                    orientedVolume: undefined,
                    box: undefined,
                    floorBased: undefined,
                };

            case SchemeKey.ORIENTED_VOLUME:
                return {
                    schemeKey: key,
                    visibility: true,
                    linkedLayerIds,
                    orientedVolume: JSON.parse(JSON.stringify(defaults.orientedVolume)),
                    convexHull: undefined,
                    box: undefined,
                    floorBased: undefined,
                };

            case SchemeKey.BOX:
                return {
                    schemeKey: key,
                    visibility: true,
                    linkedLayerIds,
                    box: JSON.parse(JSON.stringify(defaults.box)),
                    orientedVolume: undefined,
                    convexHull: undefined,
                    floorBased: undefined,
                };

            default:
                throw new Error(`Unsupported scheme key: ${key}`);
        }
    }

    public addDesignScheme(setId: string, fpId: string, schemeKey: SchemeKey, linkedLayerIds?: Partial<Record<LayerKind, string>>) {
        const designSchemeSet = this.designSchemeSetManager.getProjectById(setId);
        if (!designSchemeSet) return;

        // Return if the Footprint doesn't exist:
        const footprint = designSchemeSet.getFootprintById(fpId);
        if (!footprint) return;

        // 1) Build initial scheme state:
        const existingTemplate = this.getExistingSchemeStateTemplate(setId, schemeKey);
        const newSchemeState = existingTemplate ?? this.buildDefaultSchemeState(schemeKey, linkedLayerIds);
        newSchemeState.linkedLayerIds = linkedLayerIds;
        newSchemeState.visibility = true;

        // 1.5) Set correct border type based on DS Type (Full or Drawn):
        if (schemeKey === SchemeKey.FLOOR_BASED && newSchemeState.floorBased) {
            newSchemeState.floorBased.borderState.single =
                designSchemeSet.getType() === DesignSchemeSetType.HAND_DRAWN ? BorderSource.FOOTPRINT : BorderSource.MAIN_LAYER;
            newSchemeState.floorBased.active = true;
        }
        if (schemeKey === SchemeKey.CONVEX_HULL && newSchemeState.convexHull) {
            newSchemeState.convexHull.active = true;
        }
        if (schemeKey === SchemeKey.ORIENTED_VOLUME && newSchemeState.orientedVolume) {
            newSchemeState.orientedVolume.active = true;
        }
        if (schemeKey === SchemeKey.BOX && newSchemeState.box) {
            newSchemeState.box.active = true;
        }

        // 2) Create Config & Generate Instance:
        const initialConfig = this.getConfigFromSchemeState(schemeKey, newSchemeState);
        if (!initialConfig) return;

        const designScheme = designSchemeSet.addDesignScheme(fpId, schemeKey, initialConfig);
        const schemeId = designScheme.getID();

        if (schemeKey === SchemeKey.FLOOR_BASED && designScheme instanceof FloorBased) {
            designScheme.fitBuildingHeightToSensors();
            newSchemeState.floorBased = JSON.parse(JSON.stringify(designScheme.getRenderer().getConfig()));
        }

        // Attach listener before first store write so initial config is already updated:
        this.subscribeScheme(designScheme);
        this.updateDesignScheme(setId, fpId, schemeId, newSchemeState);
        this.refreshDesignSchemePostBlocks({
            refreshGeometry: true,
            refreshSensors: true,
        });
    }

    public addFootprint(setId: string, points: FVector[]) {
        // Return if the DesignSchemeSet doesn't exist:
        const designSchemeSet = this.designSchemeSetManager.getProjectById(setId);
        if (!designSchemeSet) return;

        // Create instance:
        const footprint = designSchemeSet.addFootprint(points);
        const fpId = footprint.id;

        // Add to State with default values:
        this.update(
            {
                sets: {
                    [setId]: {
                        footprints: {
                            [fpId]: {
                                polygon: footprint.points,
                                designSchemes: {},
                            },
                        },
                    },
                },
            },
            {
                refreshPostBlocks: {
                    refreshGeometry: true,
                    refreshSensors: true,
                },
            }
        );
    }

    public addDesignSchemeSet(name: string, type: DesignSchemeSetType) {
        // Create instance:
        const designSchemeSet = this.designSchemeSetManager.addProject(name, type);
        const setId = designSchemeSet.getID();

        // Add to State with default values:
        const newSet: DesignSchemeSetState = {
            grid: requireApp().gridId,
            id: setId,
            name,
            type,
            visibility: true,
            footprints: {},
            selectedFootprintId: null,
            consistent: true,
            outterSP: true,
            innerSP: true,
            shadow: false,
            keepout: false,
            closeToDesignSchemes: false,
            refreshShadows: false,
        };

        this.update({
            sets: {
                [setId]: newSet,
            },
        });

        // Return the created instance:
        return designSchemeSet;
    }

    // =====================================
    // GETTERS
    // =====================================

    public getDesignScheme(schemeId: string) {
        return this.designSchemeSetManager.getDesignSchemeByID(schemeId);
    }

    public getDesignSchemesByFootprint(setId: string, fpId: string) {
        const designSchemeSet = this.getDesignSchemeSet(setId);
        return designSchemeSet?.getDesignSchemesByFootprint(fpId);
    }

    public getDesignSchemes(setId: string) {
        const designSchemeSet = this.getDesignSchemeSet(setId);
        return designSchemeSet?.getDesignSchemes();
    }

    public getFootprints(setId: string) {
        return this.designSchemeSetManager.getProjectById(setId)?.getFootprints();
    }

    public getDesignSchemeSet(setId: string) {
        return this.designSchemeSetManager.getProjectById(setId);
    }

    public getDesignSchemeSets() {
        return this.designSchemeSetManager.getProjects();
    }

    // =====================================
    // OTHER HELPERS
    // =====================================

    private refreshDesignSchemePostBlocks(options: RefreshDesignSchemePostBlocksOptions = {}) {
        return this.postBlockSync.refreshDesignSchemePostBlocks((selectedSetId) => this.getDesignSchemes(selectedSetId), options);
    }

    public refreshDesignSchemes(pipelineResult: PipelineExecutionResult, layerId: string): boolean {
        this.designSchemeSetManager.update(pipelineResult, layerId);
        const store = usePersistentStore.getState();
        const selectedSetId = store.designSchemes.selectedSetId;
        const selectedSetState = selectedSetId ? store.designSchemes.sets[selectedSetId] : undefined;

        if (!selectedSetId || !selectedSetState) return false;

        return this.refreshDesignSchemePostBlocks({
            refreshGeometry: !!selectedSetState.shadow || !!selectedSetState.keepout || !!selectedSetState.closeToDesignSchemes,
            refreshSensors: false,
        });
    }

    // Sync post blocks after geometry is ready (due to asnych generation):
    public refreshPostBlocksWhenSetGeometryReady(setId: string, refreshSensors: boolean = true): void {
        const ticket = (this.refreshGeomRequestBySet.get(setId) ?? 0) + 1;
        this.refreshGeomRequestBySet.set(setId, ticket);

        // Refresh function that checks if the request is valid and the set is still selected:
        const runIfCurrent = () => {
            // 1. Check if request is still current:
            if (this.refreshGeomRequestBySet.get(setId) !== ticket) return;

            // 2. Check if set is still selected:
            const selectedSetId = this.store.designSchemes.selectedSetId;
            const selectedSetState = selectedSetId ? this.store.designSchemes.sets[selectedSetId] : undefined;
            if (selectedSetId !== setId || !selectedSetState) return;

            const shouldRefreshGeometry =
                !!selectedSetState.shadow || !!selectedSetState.keepout || !!selectedSetState.closeToDesignSchemes;
            this.refreshDesignSchemePostBlocks({
                refreshGeometry: shouldRefreshGeometry,
                refreshSensors,
            });
        };

        // Refresh blocks when Geometry is ready:
        Promise.resolve().then(() => {
            // 1. If a newer refresh request has been made or set is not selected, skip:
            if (this.refreshGeomRequestBySet.get(setId) !== ticket) return;

            const selectedSetId = this.store.designSchemes.selectedSetId;
            if (selectedSetId !== setId) return;

            // 2.1. Get renderers that are currently generating:
            const renderers = (this.getDesignSchemes(setId) ?? [])
                .map((scheme) => scheme.getRenderer())
                .filter((renderer) => !!renderer && renderer.isGenerating());

            // 2.2. If nothing is generating, refresh immediately once:
            if (renderers.length === 0) {
                runIfCurrent();
                return;
            }

            // 3. Callback that waits for all generating renderers to complete, then refreshs once:
            let remaining = renderers.length;
            const unsubs: Array<() => void> = [];
            const onOneCompleted = () => {
                // 3.1. Decrease remaining renderers count, if some are still generating, wait:
                remaining -= 1;
                if (remaining > 0) return;

                // 3.2. All renderers have completed, refresh and unsubscribe:
                unsubs.forEach((unsub) => unsub());
                runIfCurrent();
            };

            // 4. Subscribe to onGenerationComplete with the onOneCompleted callback:
            renderers.forEach((renderer) => {
                const unsub = renderer.onGenerationComplete(onOneCompleted);
                unsubs.push(unsub);
            });
        });
    }

    public ensureActiveScheme(setId: string, fpId: string, schemeKey: SchemeKey) {
        const schemes = this.getDesignSchemesByFootprint(setId, fpId) ?? [];

        // 1) Find existing scheme of this type
        let target = schemes.find((ds) => ds.getRendererKey() === schemeKey);

        // 2) Create if missing
        if (!target) {
            this.addDesignScheme(setId, fpId, schemeKey);
            target = this.getDesignSchemesByFootprint(setId, fpId)?.find((ds) => ds.getRendererKey() === schemeKey);
        }

        if (!target) return;

        // 3) Enforce SINGLE visible scheme (this is the critical part)
        schemes.forEach((ds) => {
            const isTarget = ds.getID() === target!.getID();

            this.updateDesignScheme(setId, fpId, ds.getID(), {
                visibility: isTarget,
            });

            const key = ds.getRendererKey();
            if (key === SchemeKey.FLOOR_BASED) {
                this.updateDesignScheme(setId, fpId, ds.getID(), {
                    floorBased: { active: isTarget },
                });
            } else if (key === SchemeKey.CONVEX_HULL) {
                this.updateDesignScheme(setId, fpId, ds.getID(), {
                    convexHull: { active: isTarget },
                });
            } else if (key === SchemeKey.ORIENTED_VOLUME) {
                this.updateDesignScheme(setId, fpId, ds.getID(), {
                    orientedVolume: { active: isTarget },
                });
            } else if (key === SchemeKey.BOX) {
                this.updateDesignScheme(setId, fpId, ds.getID(), {
                    box: { active: isTarget },
                });
            }
        });
    }
}

export default DesignSchemeController;
